/**
 * GPS -> Sri Lankan district, offline, with no paid geocoding API.
 *
 * Boundaries come from geoBoundaries ADM2 (OpenStreetMap derived, ODbL 1.0),
 * simplified to ~90 m and rounded to 4 decimal places so the whole country fits
 * in ~165 KB. Regenerate with `npm run districts` (see scripts/).
 *
 * The file lives in `public/data/` rather than being imported into the bundle
 * so that it is precached by the service worker as a separate asset and does
 * not inflate the main JS chunk.
 */

export const DISTRICTS_GEOJSON_URL = '/data/sri-lanka-districts.geojson';

export interface DistrictFeature {
  type: 'Feature';
  properties: { district: string; code: string | null };
  geometry:
    | { type: 'Polygon'; coordinates: number[][][] }
    | { type: 'MultiPolygon'; coordinates: number[][][][] };
}

export interface DistrictCollection {
  type: 'FeatureCollection';
  features: DistrictFeature[];
}

type BBox = [minLng: number, minLat: number, maxLng: number, maxLat: number];

/** Ray casting. `ring` is a closed array of [lng, lat] pairs. */
export function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersects = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** First ring is the outer boundary; any further rings are holes. */
export function pointInPolygon(lng: number, lat: number, rings: number[][][]): boolean {
  if (rings.length === 0 || !pointInRing(lng, lat, rings[0])) return false;
  for (let i = 1; i < rings.length; i++) {
    if (pointInRing(lng, lat, rings[i])) return false;
  }
  return true;
}

function bboxOfRing(ring: number[][]): BBox {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of ring) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return [minLng, minLat, maxLng, maxLat];
}

function bboxOfFeature(feature: DistrictFeature): BBox {
  const rings =
    feature.geometry.type === 'Polygon'
      ? [feature.geometry.coordinates[0]]
      : feature.geometry.coordinates.map((poly) => poly[0]);
  let box: BBox = [Infinity, Infinity, -Infinity, -Infinity];
  for (const ring of rings) {
    const [a, b, c, d] = bboxOfRing(ring);
    box = [Math.min(box[0], a), Math.min(box[1], b), Math.max(box[2], c), Math.max(box[3], d)];
  }
  return box;
}

function inBBox(lng: number, lat: number, box: BBox): boolean {
  return lng >= box[0] && lng <= box[2] && lat >= box[1] && lat <= box[3];
}

const bboxCache = new WeakMap<DistrictFeature, BBox>();

/**
 * Pure lookup against an already-loaded collection. Exported so it can be unit
 * tested without a network or a browser.
 */
export function findDistrict(
  latitude: number,
  longitude: number,
  collection: DistrictCollection,
): string | null {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  for (const feature of collection.features) {
    let box = bboxCache.get(feature);
    if (!box) {
      box = bboxOfFeature(feature);
      bboxCache.set(feature, box);
    }
    if (!inBBox(longitude, latitude, box)) continue;

    const hit =
      feature.geometry.type === 'Polygon'
        ? pointInPolygon(longitude, latitude, feature.geometry.coordinates)
        : feature.geometry.coordinates.some((poly) => pointInPolygon(longitude, latitude, poly));
    if (hit) return feature.properties.district;
  }
  return null;
}

let cached: DistrictCollection | null = null;
let inFlight: Promise<DistrictCollection> | null = null;

/** Loads (once) and caches the bundled boundary file. */
export async function loadDistricts(): Promise<DistrictCollection> {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = fetch(DISTRICTS_GEOJSON_URL)
    .then(async (response) => {
      if (!response.ok) throw new Error(`District data unavailable (${response.status})`);
      const data = (await response.json()) as DistrictCollection;
      cached = data;
      return data;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * The public entry point used by the UI.
 * Returns null when the point is outside every district (e.g. offshore) or when
 * the boundary file could not be loaded.
 */
export async function getDistrictFromCoordinates(
  latitude: number,
  longitude: number,
): Promise<string | null> {
  try {
    const collection = await loadDistricts();
    return findDistrict(latitude, longitude, collection);
  } catch (error) {
    console.warn('District lookup failed', error);
    return null;
  }
}

/** Test seam: lets tests inject a collection and skip fetch entirely. */
export function __setDistrictCache(collection: DistrictCollection | null): void {
  cached = collection;
}
